/**
 * Reprojection of the map parameters depends on OL and proj4.
 * It is only useful when OL is loaded.
 * As a consequence it is placed in this specific file.
 * This allows to save ~120kB in the main bundle.
 */

import type {Coordinate} from 'ol/coordinate';
import {fromEPSG21781, fromEPSG4326, insideEpsg2056} from './proj.ts';
import {SWISSTOPO_PIXELS_PER_METER} from '../helpers/print.ts';

export function reprojectSearchParams(params: URLSearchParams) {
  const x = params.get('X');
  const y = params.get('Y');
  let lon = params.get('lon_i');
  let lat = params.get('lat_i');
  const scale = params.get('scale');
  let coordinate: Coordinate | undefined;

  if (x !== null && y !== null) {
    // coordinates in EPSG:21781
    coordinate = fromEPSG21781([+x, +y]);
    params.delete('X');
    params.delete('Y');
  }
  if (lon !== null && lat !== null) {
    // coordinates in EPSG:4326 with no "." sign.
    // Because longitude for switzerland range from ~5.9 to ~10.4;
    // 1 or 2 digits represents the "whole number" and the rest is
    // the decimal fraction.
    // Example: 8051260 -> 1 digit  -> lon:  8.051260
    //         10079325 -> 2 digits -> lon: 10.079325
    const digits = lon.substring(0, 1) === '1' ? 2 : 1;
    lon = `${lon.substring(0, digits)}.${lon.substring(
      digits,
      lon.length - digits,
    )}`;
    // no need to check the number of digits, it's always 2.
    // Example: 47391355 -> 2 digits -> lat: 47.391355
    lat = `${lat.substring(0, 2)}.${lat.substring(2, lat.length - 2)}`;

    coordinate = fromEPSG4326([+lon, +lat]);
    params.delete('lon_i');
    params.delete('lat_i');
  }

  if (scale !== null) {
    params.delete('scale');
    if (!params.has('resolution')) {
      params.set(
        'resolution',
        (+scale / SWISSTOPO_PIXELS_PER_METER).toString(),
      );
    }
  }

  // validate values
  if (coordinate && insideEpsg2056({e: coordinate[0], n: coordinate[1]})) {
    params.set('E', coordinate[0].toFixed(0));
    params.set('N', coordinate[1].toFixed(0));
  }
}
